<template>
  <component
    :is="tag"
    class="btn-wrap relative text-sm bg-white border rounded-lg shadow-md transition cursor-pointer"
    :class="[className, sizes[size], widths[width], types[type], variants[variant], iconPos, padding]"
    :type="buttonType"
    :disabled="disabled"
    @click="click"
  >
    <div class="flex items-center">
      <span v-if="icon !== '' && iconPos === 'left'" class="btn-icon left w-4 h-4 flex items-center" :class="iconClass">
        <img :src="iconUrl" alt="btn icon" class="inline w-auto h-auto" />
      </span>
      <span class="text flex-auto text-center">
        <slot></slot>
      </span>
      <span
        v-if="icon !== '' && iconPos === 'right'"
        class="btn-icon right w-4 h-4 flex items-center"
        :class="iconClass"
      >
        <img :src="iconUrl" alt="btn icon" class="inline w-auto h-auto" />
      </span>
    </div>
  </component>
</template>

<script>
export default {
  name: 'ButtonCommon',
  props: {
    tag: {
      type: String,
      default: 'button',
      validator(value) {
        return ['button', 'a'].includes(value)
      },
    },
    icon: {
      type: String,
      default: '',
    },
    iconLocal: {
      type: String,
      default: 'member',
    },
    iconPos: {
      type: String,
      default: 'left',
    },
    iconClass: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'normal',
    },
    size: {
      type: String,
      default: 'normal',
    },
    className: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: 'normal',
    },
    width: {
      type: String,
      default: 'normal',
    },
    onClick: {
      type: [Function, null],
      default: null,
    },
    buttonType: {
      type: String,
      default: 'button',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    padding: {
      type: String,
      default: 'px-8',
    },
  },
  data() {
    return {
      variants: {
        gray: 'btn-gray',
        error: 'btn-error',
        delete: 'btn-delete',
        darkGreen: 'btn-dark-green',
        green: 'btn-green',
        white: 'btn-white',
        normal: '',
        cancel: 'btn-cancel',
        submit: 'btn-submit',
      },
      types: {
        outline: 'btn-outline border',
        normal: 'btn-primary border border-transparent text-white',
      },
      sizes: {
        sm: 'py-1.5 text-xs',
        normal: 'py-3 text-sm',
        lg: 'py-5 text-base',
      },
      widths: {
        sm: '',
        normal: '',
        medium: 'min-w-44',
        lg: 'md:w-60',
        full: 'w-full',
      },
    }
  },
  computed: {
    iconUrl() {
      return '/images/' + this.iconLocal + '/icon-btn-' + this.icon + '.svg'
    },
  },
  methods: {
    click($event) {
      if (this.onClick && typeof this.onClick === 'function') {
        this.onClick($event)
      } else {
        this.$emit('click', $event)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-outline {
  color: var(--color-primary);
  border-color: var(--color-primary);
  border-style: solid;
  &:hover {
    background: var(--btn-outline-hover-bg);
  }
}
.btn-primary {
  background: var(--color-primary);
  color: #fff;
  &:hover {
    background: var(--color-green-dark);
    border-color: var(--color-green-dark);
  }
  &:disabled {
    background: #d1d5db;
    border-color: #d1d5db;
    color: #fff;
    cursor: not-allowed;
  }
}
.btn-dark-green {
  background: var(--color-green-dark);
  &:hover {
    background: var(--color-primary);
    border-color: var(--color-primary);
  }
}
.btn-gray {
  background: var(--btn-gray-bg);
  color: #fff;
  &:hover {
    background: var(--btn-gray-hover-bg);
    border-color: var(--btn-gray-hover-bg);
  }
}
.btn-error {
  background: var(--color-error);
  border-color: var(--color-error);
  color: #fff;
  &:hover {
    background: var(--color-error-hover);
    border-color: var(--color-error-hover);
  }
  &:disabled {
    background-color: #fc8181;
    cursor: not-allowed;
  }
}

.btn-delete {
  background: #e02424;
  border-color: #e02424;
  color: #fff;
  &:hover {
    background: #e02424;
    border-color: #e02424;
    opacity: 0.5;
  }
  &:disabled {
    background: #d1d5db;
    border-color: #d1d5db;
    color: #fff;
    cursor: not-allowed;
  }
}
.btn-green {
  background: #3b7e34;
  border-color: #3b7e34;
  color: #fff;
  &:hover {
    background-color: #3b7e34;
    opacity: 0.5;
  }
  &:disabled {
    background-color: #3b7e34;
    opacity: 0.5;
  }
}

.btn-white {
  background: #fff;
  border-color: #3b7e34;
  color: #3b7e34;
  &:hover {
    background: #f5f5f5;
    color: #3b7e34;
  }
  &:disabled {
    background: #fff;
    border-color: #ccc;
    color: #ccc;
    cursor: not-allowed;
  }
}

.btn-gray {
  background: var(--color-gray);
  border-color: var(--color-gray);
  color: #fff;
  &:hover {
    background: var(--color-gray-hover);
    border-color: var(--color-gray-hover);
  }
  &:disabled {
    cursor: default;
    &:hover {
      background: var(--color-gray);
      border-color: var(--color-gray);
    }
  }
}
.btn-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translate(0, -50%);
  &.right {
    left: auto;
    right: 5px;
  }
}
.btn-cancel {
  background: #fff;
  border-color: #d1d5db;
  color: #222222;
  font-weight: 700;
  &:hover {
    opacity: 0.5;
    background: #fff;
  }
}
.btn-submit {
  background: #3b7e34;
  border-color: #3b7e34;
  color: #fff;
  font-weight: 700;
  &:disabled {
    cursor: not-allowed;
    background: #d1d5db;
    border-color: #d1d5db;
  }
}
.px-4-important {
  padding-left: 1rem !important;
  padding-right: 1rem !important;
}
</style>
